import { useState } from 'react';
import SecondaryPageActions from '../../components/secondary-page-actions/SecondaryPageActions';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { ClientQueryStatusEnum } from '../../types/client-query';
import { SubscriptionTier } from '../../types/firm';
import { InternalCommentStatusEnum } from '../../types/internal-comment';
import { WorkpaperRoleEnum } from '../../types/workpaper';
import { WorksheetStatusEnum, WorksheetTypeEnum, WorksheetWithAccount, Worksheet } from '../../types/worksheet';
import WorksheetExcludeConfirm from '../worksheet/WorksheetExcludeConfirm';
import WorksheetIncludeConfirm from '../worksheet/WorksheetIncludeConfirm';
import WorksheetReviewConfirm from '../worksheet/WorksheetReviewConfirm';
import AddAssigneesDialog from './AddAssigneesDialog';
import RemoveAssigneesDialog from './RemoveAssigneesDialog';
import WorksheetTypeChangeConfirm from '../worksheet/WorksheetTypeChangeConfirm';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

import { Modal, ButtonV2 } from '@cimplico/components';
import { isNullOrUndefined } from '../new-workpaper/utils';
import LinkPriorWorksheetsContainer from '../carry-forward/LinkPriorWorksheetsContainer';
import { toastNotification } from '../../components/toast/Toast';
import BulkCarryForwardResourcesConfirm from '../carry-forward/BulkCarryForwardResourcesConfirm';
/**
 * Filters the worksheets to only those that can be excluded.
 *
 * @param worksheets - The worksheets to filter.
 * @returns The filtered worksheets.
 */
function filterCanExclude(worksheets: WorksheetWithAccount[]): WorksheetWithAccount[] {
    return worksheets.filter((ws) => {
        // The worksheet can be excluded if:
        // - It is not excluded already
        // - All internal comments are resolved
        // - All client queries are resolved
        return (
            ws.status !== WorksheetStatusEnum.EXCLUDED &&
            ws.internalComments.every((ic) => ic.status === InternalCommentStatusEnum.RESOLVED) &&
            ws.clientQueries.every((cq) => cq.status === ClientQueryStatusEnum.RESOLVED)
        );
    });
}

/**
 * Filters the worksheets to only those that can be included.
 *
 * @param worksheets - The worksheets to filter.
 * @returns The filtered worksheets.
 */
function filterCanInclude(worksheets: WorksheetWithAccount[]): WorksheetWithAccount[] {
    return worksheets.filter((ws) => {
        // The worksheet can be included if:
        // - It is excluded
        return ws.status === WorksheetStatusEnum.EXCLUDED;
    });
}

/**
 * Filters the worksheets to only those that can be reviewed.
 *
 * @param worksheets - The worksheets to filter.
 * @returns The filtered worksheets.
 */
function filterCanReview(worksheets: WorksheetWithAccount[]): WorksheetWithAccount[] {
    return worksheets.filter((ws) => {
        // The worksheet can be reviewed if:
        // - It is awaiting review
        return ws.status === WorksheetStatusEnum.AWAITING_REVIEW;
    });
}

/**
 * Filters the worksheets to only those that can have users assigned.
 *
 * @param worksheets - The worksheets to filter.
 * @returns The filtered worksheets.
 */
function filterCanAssign(worksheets: WorksheetWithAccount[]): WorksheetWithAccount[] {
    return worksheets.filter((ws) => {
        // The worksheet can be assigned to if:
        // - It is not excluded
        return ws.status !== WorksheetStatusEnum.EXCLUDED;
    });
}

/**
 * Filters the worksheets to only those that can have type changed to reconciliation.
 *
 * @param worksheets - The worksheets to filter.
 * @returns The filtered worksheets.
 */
function filterCanChangeToReconciliationType(worksheets: WorksheetWithAccount[]): WorksheetWithAccount[] {
    return worksheets.filter((ws) => {
        // The worksheet type can be changed if:
        // - It is not excluded
        // - It is currently a generic type
        return ws.status !== WorksheetStatusEnum.EXCLUDED && ws.type === WorksheetTypeEnum.GENERIC;
    });
}

/**
 * Filters the worksheets to only those that can have type changed to generic.
 *
 * @param worksheets - The worksheets to filter.
 * @returns The filtered worksheets.
 */
function filterCanChangeToGenericType(worksheets: WorksheetWithAccount[]): WorksheetWithAccount[] {
    return worksheets.filter((ws) => {
        // The worksheet type can be changed if:
        // - It is not excluded
        // - It is currently a reconciliation type
        return ws.status !== WorksheetStatusEnum.EXCLUDED && ws.type === WorksheetTypeEnum.RECONCILIATION;
    });
}

/**
 * Filters the worksheets to only those that are able to be linked to a prior worksheet
 * @param worksheets the worksheets to filter
 * @param priorWorkpaperId if undefined, no worksheets are valid
 * @returns the filtered worksheets
 */
function filterCanLinkToPrior(worksheets: WorksheetWithAccount[], priorWorkpaperId?: string): WorksheetWithAccount[] {
    if (isNullOrUndefined(priorWorkpaperId)) return [];
    return worksheets.filter((ws) => isNullOrUndefined(ws.priorWorksheetId));
}

/**
 * Filters the worksheets to only those that are already linked to a prior worksheet
 * @param worksheets the worksheets to filter
 * @param priorWorkpaperId if undefined, no worksheets are valid
 * @returns worksheets that have a priorWorksheetId.
 */
function filterCanCarryForward(worksheets: WorksheetWithAccount[], priorWorkpaperId?: string): WorksheetWithAccount[] {
    if (isNullOrUndefined(priorWorkpaperId)) return [];
    return worksheets.filter((ws) => !isNullOrUndefined(ws.priorWorksheetId));
}

export type TrialBalanceAction =
    | 'EXCLUDE'
    | 'INCLUDE'
    | 'ADD_ASSIGNEES'
    | 'REMOVE_ASSIGNEES'
    | 'UPGRADE'
    | 'REVIEW'
    | 'CHANGE_TO_GENERIC'
    | 'CHANGE_TO_RECONCILIATION'
    | 'LINK_TO_PRIOR'
    | 'CARRY_FORWARD';

type TrialBalanceBulkActionsProps = {
    /**
     * The worksheets to perform bulk actions on.
     */
    worksheets: WorksheetWithAccount[];

    /**
     * A callback when a bulk action is triggered.
     */
    onBulkAction: () => void;

    /**
     * A callback when a bulk action errors.
     */
    onBulkActionError: (errors: string[]) => void;

    /**
     * A callback when the user requests to update the trial balance
     */
    onUpdate: () => void;
};

function TrialBalanceBulkActions({ worksheets, onBulkAction, onBulkActionError }: TrialBalanceBulkActionsProps): JSX.Element {
    const workpaper = useWorkpaperContext();
    const featureAccess = { canBulkUpdateAssignees: useHasFeatureAccess('worksheet-update-assignees:write') };

    const [action, setAction] = useState<TrialBalanceAction | undefined>();

    const canAssign = filterCanAssign(worksheets);
    const canExclude = filterCanExclude(worksheets);
    const canInclude = filterCanInclude(worksheets);
    const canReview = filterCanReview(worksheets);
    const canChangeToReconciliationType = filterCanChangeToReconciliationType(worksheets);
    const canChangeToGenericType = filterCanChangeToGenericType(worksheets);
    const canLinkToPrior = filterCanLinkToPrior(worksheets, workpaper.priorWorkpaperId) as Worksheet[];
    const canCarryForward = filterCanCarryForward(worksheets, workpaper.priorWorkpaperId) as Worksheet[];

    const userCanReview = [WorkpaperRoleEnum.REVIEWER, WorkpaperRoleEnum.APPROVER].includes(workpaper.role);

    /**
     * Sets state to trigger the **AddAssigneesDialog**  or **RemoveAssigneesDialog** component.
     * If the firm subscription is a not a high enough tier, an upgrade prompt will display instead
     */
    function triggerAssignConfirmation(action: 'ADD_ASSIGNEES' | 'REMOVE_ASSIGNEES'): void {
        if (!featureAccess.canBulkUpdateAssignees) {
            setAction('UPGRADE');
        } else {
            setAction(action);
        }
    }

    /**
     * Callback whenever a dialog finishes its action and closes; signalling the user has finished their desired action.
     */
    function handleDialogClose(): void {
        setAction(undefined);
        onBulkAction();
    }

    /**
     * Formats the errors from a bulk action into a more readable format.
     *
     * @param errors - The errors from the bulk action, keyed by worksheet ID.
     * @returns The formatted errors, with the worksheet name included.
     */
    function formatErrors(errors: { [key: string]: string }): string[] {
        return Object.keys(errors).map((id) => {
            const matchingWs = worksheets.find((ws) => ws.id === id);

            if (!matchingWs) {
                return `${id}: ${errors[id]}`;
            }
            return `${matchingWs.name}: ${errors[id]}`;
        });
    }

    return (
        <>
            {action === 'ADD_ASSIGNEES' && <AddAssigneesDialog worksheets={canAssign} onClose={handleDialogClose} />}
            {action === 'REMOVE_ASSIGNEES' && <RemoveAssigneesDialog worksheets={canAssign} onClose={handleDialogClose} />}
            {action === 'UPGRADE' && <UpgradePrompt onClose={(): void => setAction(undefined)} targetTier={SubscriptionTier.ESSENTIAL} />}
            {action === 'INCLUDE' && (
                <WorksheetIncludeConfirm
                    worksheetIds={canInclude.map((ws) => ws.id)}
                    onClose={(): void => setAction(undefined)}
                    onInclude={handleDialogClose}
                    onError={(e): void => onBulkActionError(formatErrors(e))}
                />
            )}
            {action === 'EXCLUDE' && (
                <WorksheetExcludeConfirm
                    worksheetIds={canExclude.map((ws) => ws.id)}
                    onClose={(): void => setAction(undefined)}
                    onExclude={handleDialogClose}
                    onError={(e): void => onBulkActionError(formatErrors(e))}
                />
            )}
            {action === 'REVIEW' && (
                <WorksheetReviewConfirm
                    worksheetIds={canReview.map((ws) => ws.id)}
                    onClose={(): void => setAction(undefined)}
                    onReview={handleDialogClose}
                    onError={(e): void => onBulkActionError(formatErrors(e))}
                />
            )}
            {action === 'CHANGE_TO_RECONCILIATION' && (
                <WorksheetTypeChangeConfirm
                    worksheetIds={canChangeToReconciliationType.map((ws) => ws.id)}
                    newType={WorksheetTypeEnum.RECONCILIATION}
                    onClose={(): void => setAction(undefined)}
                    onUpdate={handleDialogClose}
                    onError={(e): void => onBulkActionError(formatErrors(e))}
                />
            )}
            {action === 'CHANGE_TO_GENERIC' && (
                <WorksheetTypeChangeConfirm
                    worksheetIds={canChangeToGenericType.map((ws) => ws.id)}
                    newType={WorksheetTypeEnum.GENERIC}
                    onClose={(): void => setAction(undefined)}
                    onUpdate={handleDialogClose}
                    onError={(e): void => onBulkActionError(formatErrors(e))}
                />
            )}
            {action === 'CARRY_FORWARD' && workpaper.priorWorkpaperId && (
                <BulkCarryForwardResourcesConfirm
                    priorWorkpaperId={workpaper.priorWorkpaperId}
                    selectedWorksheets={canCarryForward}
                    onClose={(): void => setAction(undefined)}
                    onCarryForward={handleDialogClose}
                />
            )}

            {action === 'LINK_TO_PRIOR' && (
                <>
                    {isNullOrUndefined(workpaper.priorWorkpaperId) ? (
                        toastNotification('error', 'This workpaper is not linked to a prior workpaper.')
                    ) : (
                        <Modal isOpen onClose={(): void => setAction(undefined)}>
                            <div className="py-8 px-12 w-[1200px] h-[800px] flex flex-col">
                                <div className="overflow-auto grow px-1">
                                    <LinkPriorWorksheetsContainer
                                        priorWorkpaperId={workpaper.priorWorkpaperId}
                                        selectedWorksheets={canLinkToPrior}
                                        accountWorksheets
                                        onLink={handleDialogClose}
                                    />
                                </div>
                                <div className="border-t pt-4 mt-2 flex justify-end gap-2">
                                    <ButtonV2 onClick={(): void => setAction(undefined)} label="Close" size="sm" type="primary" />
                                </div>
                            </div>
                        </Modal>
                    )}
                </>
            )}
            <div>
                {worksheets.length > 0 && (
                    <>
                        <SecondaryPageActions maxActionsCount={4}>
                            <SecondaryPageActions.Button
                                label={`Assign (${canAssign.length})`}
                                onClick={(): void => triggerAssignConfirmation('ADD_ASSIGNEES')}
                                disabled={workpaper.readOnly || canAssign.length === 0}
                            />
                            <SecondaryPageActions.Button
                                label={`Unassign (${canAssign.length})`}
                                onClick={(): void => triggerAssignConfirmation('REMOVE_ASSIGNEES')}
                                disabled={workpaper.readOnly || canAssign.length === 0}
                            />
                            <SecondaryPageActions.Button
                                label={`Include (${canInclude.length})`}
                                onClick={(): void => setAction('INCLUDE')}
                                disabled={workpaper.readOnly || canInclude.length === 0}
                            />
                            <SecondaryPageActions.Button
                                label={`Exclude (${canExclude.length})`}
                                onClick={(): void => setAction('EXCLUDE')}
                                disabled={workpaper.readOnly || canExclude.length === 0}
                            />
                            <SecondaryPageActions.Button
                                label={`Change to Reconciliation Worksheet (${canChangeToReconciliationType.length})`}
                                disabled={workpaper.readOnly || canChangeToReconciliationType.length === 0}
                                onClick={(): void => setAction('CHANGE_TO_RECONCILIATION')}
                            />
                            <SecondaryPageActions.Button
                                label={`Change to Generic Worksheet (${canChangeToGenericType.length})`}
                                disabled={workpaper.readOnly || canChangeToGenericType.length === 0}
                                onClick={(): void => setAction('CHANGE_TO_GENERIC')}
                            />
                            <SecondaryPageActions.Button
                                label={`Carry Forward (${canCarryForward.length})`}
                                disabled={
                                    workpaper.readOnly || canCarryForward.length === 0 || isNullOrUndefined(workpaper.priorWorkpaperId)
                                }
                                disabledHint={
                                    !workpaper.priorWorkpaperId
                                        ? 'This workpaper is not linked to a prior workpaper'
                                        : canCarryForward.length === 0
                                        ? 'No selected worksheet has been linked to a prior worksheet'
                                        : undefined
                                }
                                onClick={(): void => setAction('CARRY_FORWARD')}
                                staffOnly={true}
                            />
                            <SecondaryPageActions.Button
                                label={`Link to Prior (${canLinkToPrior.length})`}
                                disabled={
                                    workpaper.readOnly || canLinkToPrior.length === 0 || isNullOrUndefined(workpaper.priorWorkpaperId)
                                }
                                onClick={(): void => setAction('LINK_TO_PRIOR')}
                                staffOnly={true}
                            />
                            <SecondaryPageActions.Button
                                label={`Review (${canReview.length})`}
                                type="primary"
                                disabled={workpaper.readOnly || !userCanReview || canReview.length === 0}
                                onClick={(): void => setAction('REVIEW')}
                                disabledHint={!userCanReview ? 'Only reviewers or approvers can review worksheets.' : undefined}
                                collapsible={false}
                            />
                        </SecondaryPageActions>
                    </>
                )}
            </div>
        </>
    );
}

export default TrialBalanceBulkActions;
